import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ArrowLeft, Lock, Send } from "lucide-react";
import { toast } from "react-toastify";
import "./staking.css";

const API_URL = process.env.REACT_APP_API_URL;
const DAY_MS = 24 * 60 * 60 * 1000;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtDate = (d) =>
  new Date(d).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
const unlockDate = (days) => new Date(Date.now() + days * DAY_MS);

const Staking = ({ validPoints, onClose, onChanged }) => {
  const [overview, setOverview] = useState(null);
  const [stakes, setStakes] = useState([]);
  const [tab, setTab] = useState("stake"); // "stake" | "mine"
  const [planId, setPlanId] = useState("6m");
  const [amount, setAmount] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [unstakingId, setUnstakingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [ov, my] = await Promise.all([
        axios.get(`${API_URL}/staking/overview`, authHeaders()),
        axios.get(`${API_URL}/staking/my`, authHeaders()),
      ]);
      setOverview(ov.data);
      setStakes(my.data.stakes || []);
    } catch (e) {
      const status = e.response?.status;
      const msg =
        e.response?.data?.message ||
        (status ? "Request failed." : "Network error (server unreachable).");
      console.error("Staking load error:", status, e.response?.data || e);
      setLoadError(`${status ? `Error ${status} — ` : ""}${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const level = overview?.currentLevel;
  const next = overview?.nextLevel;
  const hasLevel = Boolean(level && level.level > 0);
  const plan = overview?.plans?.find((p) => p.id === planId);

  const amt = parseInt(amount, 10);
  const validAmt = Number.isInteger(amt) && amt > 0;
  const okAmount = validAmt && overview && amt >= overview.minStake;
  // Le pourcentage ne s'applique qu'à partir de rewardMinStake (500 pts)
  const rewardApplies = okAmount && amt >= overview.rewardMinStake;
  const percent = plan && rewardApplies ? plan.rewardPercent : 0;
  const rewardPreview = okAmount ? Math.floor((amt * percent) / 100) : 0;
  const endPreview = plan ? unlockDate(plan.durationDays) : null;

  const nextGap = next ? Math.ceil(next.remaining) : 0;

  const handleAmountChange = (e) => {
    const v = e.target.value;
    if (/^\d*$/.test(v)) {
      setAmount(v);
      setError(null);
    }
  };

  const handleContinue = () => {
    if (!hasLevel) return;
    if (!okAmount) {
      setError(`Minimum stake is ${fmt(overview.minStake)} pts.`);
      return;
    }
    if (amt > validPoints) {
      setError(`You only have ${fmt(validPoints)} available pts.`);
      return;
    }
    setError(null);
    setConfirming(true);
  };

  const submitStake = async () => {
    setSubmitting(true);

    try {
      const res = await axios.post(
        `${API_URL}/staking/stake`,
        { amount: amt, planId },
        authHeaders(),
      );

      toast.info("Request sent to admin. Waiting for approval.", {
        position: "top-center",
      });

      setAmount("");
      setConfirming(false);
      setTab("mine");

      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Staking request error.", {
        position: "top-center",
      });

      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnstake = async (id) => {
    setUnstakingId(id);
    try {
      const res = await axios.post(
        `${API_URL}/staking/${id}/unstake`,
        {},
        authHeaders(),
      );
      toast.success(
        `+${fmt(res.data.returned.total)} pts returned to your wallet.`,
        { position: "top-center" },
      );
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Unstake error.", {
        position: "top-center",
      });
    } finally {
      setUnstakingId(null);
    }
  };

  return (
    <div className="infoOverlayW">
      <div className="infoModalWallet withdraw-flow-modal stk-modal">
        <div className="modal-drag" />

        <div
          className="close_icon"
          role="button"
          tabIndex={0}
          onClick={confirming ? () => setConfirming(false) : onClose}
          onKeyDown={(e) =>
            e.key === "Enter" && (confirming ? setConfirming(false) : onClose())
          }
        >
          <ArrowLeft size={13} />
        </div>

        <div className="stk-title">Staking</div>

        {loading ? (
          <div className="stk-empty">Loading…</div>
        ) : loadError || !overview ? (
          /* ───────── Erreur de chargement ───────── */
          <div className="stk-body">
            <div className="stk-error">
              {loadError || "Unable to load staking."}
            </div>
            <button
              className="stk-unstake"
              onClick={() => {
                setLoading(true);
                load();
              }}
            >
              Retry
            </button>
          </div>
        ) : confirming ? (
          /* ───────── Confirmation ───────── */
          <>
            <div className="stk-body">
              <div className="stk-confirm-amount">
                <span className="stk-confirm-val">{fmt(amt)} pts</span>
                <span className="stk-confirm-sub">
                  Request for <strong>{plan.label}</strong>
                </span>
              </div>

              <div className="stk-rows">
                <div className="stk-row">
                  <span>Reward rate ({level.name})</span>
                  <strong className="gold">{percent}%</strong>
                </div>
                <div className="stk-row">
                  <span>Reward</span>
                  <strong className="ok">+{fmt(rewardPreview)} pts</strong>
                </div>
                <div className="stk-row">
                  <span>You get back</span>
                  <strong>{fmt(amt + rewardPreview)} pts</strong>
                </div>
                <div className="stk-row">
                  <span>Unlock date</span>
                  <strong>{fmtDate(endPreview)}</strong>
                </div>
              </div>

              {!rewardApplies && (
                <div className="stk-hint">
                  No reward below {fmt(overview.rewardMinStake)} pts.
                </div>
              )}

              <div className="stk-warning">
                <Lock size={11} /> Locked until the unlock date. No early
                withdrawal.
              </div>
            </div>

            <div className="confirm-btn-stack">
              <button
                className="confirm-btn-yes"
                onClick={submitStake}
                disabled={submitting}
              >
                <Send size={13} />
                {submitting ? "Sending request…" : "Send request"}
              </button>
              <button
                className="confirm-btn-no"
                onClick={() => setConfirming(false)}
                disabled={submitting}
              >
                Back
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="stk-tabs">
              <button
                className={`stk-tab${tab === "stake" ? " is-active" : ""}`}
                onClick={() => setTab("stake")}
              >
                Stake
              </button>
              <button
                className={`stk-tab${tab === "mine" ? " is-active" : ""}`}
                onClick={() => setTab("mine")}
              >
                My stakes ({stakes.length})
              </button>
            </div>

            <div className="stk-body">
              {tab === "stake" ? (
                <>
                  {/* ───────── Niveau ───────── */}
                  <div className="stk-level">
                    <div className="stk-level-top">
                      <span className="stk-level-badge">{level.name}</span>
                      <span
                        className={`stk-level-rate${hasLevel ? "" : " is-off"}`}
                      >
                        {hasLevel
                          ? `${level.rewardPercent}% reward`
                          : "No reward yet"}
                      </span>
                    </div>
                    <div className="stk-level-sub">
                      Invested <strong>{fmt(overview.totalInvested)} DT</strong>
                    </div>
                    {next && (
                      <>
                        <div className="stk-progress">
                          <div
                            className="stk-progress-bar"
                            style={{
                              width: `${Math.min(
                                100,
                                (overview.totalInvested / next.minInvested) *
                                  100,
                              )}%`,
                            }}
                          />
                        </div>
                        <div className="stk-level-sub">
                          {fmt(nextGap)} DT more to reach {next.name} (
                          {next.rewardPercent}%)
                        </div>
                      </>
                    )}
                  </div>

                  {!hasLevel && (
                    <div className="stk-hint">
                      Reach Level 1 to start earning staking rewards.
                    </div>
                  )}

                  {/* ───────── Durée (sans pourcentage) ───────── */}
                  <div className="stk-label">Duration</div>
                  <div className="stk-seg">
                    {overview.plans.map((p) => (
                      <button
                        key={p.id}
                        className={`stk-seg-btn${
                          planId === p.id ? " is-active" : ""
                        }`}
                        onClick={() => setPlanId(p.id)}
                        disabled={!hasLevel}
                      >
                        <span className="stk-seg-name">{p.label}</span>
                        <span className="stk-seg-sub">
                          until {fmtDate(unlockDate(p.durationDays))}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* ───────── Montant ───────── */}
                  <div className="stk-label stk-label-row">
                    <span>Amount</span>
                    <span className="stk-avail">
                      Available {fmt(validPoints)} pts
                    </span>
                  </div>
                  <div className="stk-input-row">
                    <input
                      type="text"
                      inputMode="numeric"
                      className="stk-input"
                      placeholder={`Min ${fmt(overview.minStake)} pts`}
                      value={amount}
                      onChange={handleAmountChange}
                      disabled={!hasLevel}
                    />
                    <button
                      className="stk-max"
                      onClick={() => setAmount(String(validPoints))}
                      disabled={!hasLevel}
                    >
                      Max
                    </button>
                  </div>

                  {hasLevel && okAmount && (
                    <div className="stk-preview">
                      <div>
                        <span>Rate</span>
                        <strong>{percent}%</strong>
                      </div>
                      <div>
                        <span>Reward</span>
                        <strong className="ok">
                          +{fmt(rewardPreview)} pts
                        </strong>
                      </div>
                      <div>
                        <span>Unlock</span>
                        <strong>{fmtDate(endPreview)}</strong>
                      </div>
                    </div>
                  )}

                  {hasLevel && okAmount && !rewardApplies && (
                    <div className="stk-hint">
                      The reward applies from {fmt(overview.rewardMinStake)}{" "}
                      pts. Stake at least {fmt(overview.rewardMinStake)} pts to
                      earn {plan.rewardPercent}%.
                    </div>
                  )}

                  {error && <div className="stk-error">{error}</div>}
                </>
              ) : stakes.length === 0 ? (
                <div className="stk-empty">No stakes yet.</div>
              ) : (
                <div className="stk-list">
                  {stakes.map((s) => (
                    <div className="stk-item" key={s._id}>
                      <div className="stk-item-top">
                        <span className="stk-item-amount">
                          {fmt(s.amount)} pts
                        </span>
                        <span
                          className={`stk-item-status ${
                            s.status === "completed"
                              ? "is-done"
                              : s.status === "pending"
                                ? "is-pending"
                                : s.status === "rejected"
                                  ? "is-rejected"
                                  : "is-active"
                          }`}
                        >
                          {s.status === "pending"
                            ? "Pending"
                            : s.status === "active"
                              ? "Active"
                              : s.status === "completed"
                                ? "Completed"
                                : "Rejected"}
                        </span>
                      </div>
                      <div className="stk-item-sub">
                        {s.planId === "12m" ? "1 year" : "6 months"} ·{" "}
                        {s.rewardPercent}% · +{fmt(s.rewardPoints)} pts
                      </div>
                      <div className="stk-item-sub">
                        {s.status === "pending"
                          ? "Waiting for admin approval"
                          : s.status === "rejected"
                            ? "Your request was rejected"
                            : s.status === "completed"
                              ? `Returned on ${fmtDate(s.completedAt)}`
                              : `Unlocks on ${fmtDate(s.endDate)}`}
                      </div>

                      {s.status === "active" &&
                        (s.canUnstake ? (
                          <button
                            className="stk-unstake"
                            onClick={() => handleUnstake(s._id)}
                            disabled={unstakingId === s._id}
                          >
                            {unstakingId === s._id
                              ? "Processing…"
                              : `Unstake ${fmt(s.amount + s.rewardPoints)} pts`}
                          </button>
                        ) : (
                          <div className="stk-locked">
                            <Lock size={10} /> Locked · {s.daysLeft} day(s) left
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {tab === "stake" && (
              <div className="withdraw-btn-row">
                <button className="btn-cancel-w" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="btn-submit-w"
                  onClick={handleContinue}
                  disabled={!hasLevel}
                >
                  <Lock size={13} />
                  Continue
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Staking;
